/**
 * Plausible event tracking.
 *
 * Het script wordt geladen in public/index.html; de stub op window.plausible vangt
 * events op die vuren voordat het script binnen is. Faalt stil als analytics
 * geblokkeerd wordt - tracking mag nooit een gebruikersactie kapotmaken.
 */

import { DrillVisibility } from '../types/api';
import { isNative } from '../services/nativeAuth';

type PlausibleProps = Record<string, string | number | boolean>;

declare global {
  interface Window {
    plausible?: (event: string, options?: { props?: PlausibleProps }) => void;
  }
}

/**
 * De native app gaat buiten het script om. Capacitor serveert de app vanaf localhost
 * (capacitor://localhost op iOS, https://localhost op Android) en het script negeert elk
 * event van localhost, dus zonder dit telt de app helemaal niets.
 *
 * Dit is hetzelfde verzoek dat het script zelf doet, alleen met de url op het eigen domein.
 * text/plain houdt het een simpel verzoek, zonder CORS-preflight. Domein en server komen
 * uit de scripttag, zodat ze op één plek blijven staan.
 *
 * Offline gaat het event verloren; er is geen wachtrij zoals bij het live verslag zelf.
 */
function sendDirect(event: string, props?: PlausibleProps): void {
  const script = document.querySelector<HTMLScriptElement>('script[data-domain]');
  const domain = script?.dataset.domain;
  if (!script || !domain) return;

  void fetch(new URL('/api/event', script.src).toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({
      n: event,
      u: `https://${domain}${window.location.pathname}`,
      d: domain,
      r: null,
      ...(props ? { p: props } : {}),
    }),
    keepalive: true,
  }).catch(() => undefined);
}

export function trackEvent(event: string, props?: PlausibleProps): void {
  try {
    if (isNative()) {
      sendDirect(event, props);
    } else {
      window.plausible?.(event, props ? { props } : undefined);
    }
  } catch {
    // bewust genegeerd
  }
}

const platform = (): 'native' | 'web' => (isNative() ? 'native' : 'web');

/** De momenten die de activatiefunnel bepalen. */
export const events = {
  accountAangemaakt: () => trackEvent('Account Aangemaakt'),
  teamAangemaakt: () => trackEvent('Team Aangemaakt'),
  wedstrijdenGeimporteerd: (aantal: number) =>
    trackEvent('Wedstrijden Geimporteerd', { aantal }),
  opstellingGedeeld: (kanaal: string) =>
    trackEvent('Opstelling Gedeeld', { kanaal }),
  /**
   * Eigen doel naast 'Opstelling Gedeeld' met kanaal=pdf: de uitdraai is een ander
   * gebruik dan een deel-link (dug-out in plaats van appgroep) en verdient een eigen
   * conversiecijfer. Beide vuren, dus tel ze niet bij elkaar op.
   *
   * platform onderscheidt de browserdownload van het iOS-deelblad; dat laatste kan
   * resolven zonder dat de gebruiker het bestand echt bewaart.
   */
  opstellingPdfGedownload: (platform: 'web' | 'native', opstellingen: number) =>
    trackEvent('Opstelling PDF Gedownload', { platform, opstellingen }),
  /**
   * Alleen het aanmaken, niet het bijwerken: bewerken is onderhoud aan iets wat er al is
   * en zegt niets over of de oefeningen aanslaan.
   *
   * `zichtbaarheid` is de vraag erachter waar we het antwoord niet op weten: maken coaches
   * oefeningen voor zichzelf, of delen ze die met hun team of met iedereen? Dat is precies
   * het verschil tussen een notitieblok en een bibliotheek.
   */
  oefeningAangemaakt: (zichtbaarheid: DrillVisibility) =>
    trackEvent('Oefening Aangemaakt', { zichtbaarheid }),
  /**
   * Alleen het toevoegen. Een favoriet weghalen is geen tegengesteld doel maar gewoon
   * opruimen, en zou als eigen gebeurtenis het cijfer alleen maar troebel maken.
   *
   * `bron` scheidt het hartje op de kaart in de lijst van dat op de detailpagina: het
   * eerste is bladeren, het tweede is na het lezen besluiten.
   */
  oefeningFavoriet: (bron: 'lijst' | 'detail') =>
    trackEvent('Oefening Favoriet', { bron }),
  // Het live verslag, als trechter: geopend, gestart, afgerond. Het verschil tussen de
  // eerste twee zegt wie alleen komt kijken; tussen de laatste twee wie halverwege afhaakt.
  // `platform` laat zien of het langs de lijn in de app gebeurt of in de browser.

  /** Eén keer per bezoek. `rol` scheidt de coach die bijhoudt van wie alleen meeleest. */
  liveVerslagGeopend: (rol: 'schrijver' | 'meelezer') =>
    trackEvent('Live Verslag Geopend', { rol, platform: platform() }),
  /** `format` als "4x17:30", zodat afwijkende speeltijden opvallen. */
  liveVerslagGestart: (format: string) =>
    trackEvent('Live Verslag Gestart', { format, platform: platform() }),
  /**
   * Alleen het beëindigen zelf; een uitslag achteraf bijwerken is geen tweede afronding.
   * `uitslagOpgeslagen` zegt of de live stand ook als uitslag de wedstrijd in ging.
   */
  liveVerslagAfgerond: (uitslagOpgeslagen: boolean) =>
    trackEvent('Live Verslag Afgerond', { uitslagOpgeslagen, platform: platform() }),
  /**
   * Een fan opent de volgpagina, één keer per wedstrijd per bezoek. `moment` scheidt meekijken
   * tijdens de wedstrijd van terugkijken na afloop.
   */
  liveVerslagGevolgd: (moment: 'live' | 'terugkijken') =>
    trackEvent('Live Verslag Gevolgd', { moment, platform: platform() }),
};
